import { Component, OnInit, Inject, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { OrderService } from 'src/app/order.service';
import { Store } from 'src/app/models/store';

export interface ReorderSheetData {
  storeId: string;
  userHash: string;
  store: Store;
}

/**
 * "Reorder" from the menu's sticky bar: the shopper's recent orders, each with its date, what was
 * in it and a button that copies it into a fresh cart. Two taps from anywhere in the menu, without
 * leaving the menu — the old route was Order History, open the receipt, scroll past it, reorder.
 */
@Component({
  selector: 'reorder-sheet',
  templateUrl: './reorder-sheet.component.html',
  styleUrls: ['./reorder-sheet.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReorderSheetComponent implements OnInit {

  /** The few most recent orders. The full list stays one tap away on the history page. */
  readonly limit = 5;
  orders: any[] = null;
  loadFailed = false;
  /** Order currently being copied, so a double tap cannot open two carts. */
  reorderingHash: string = null;

  constructor(
    private dialogRef: MatDialogRef<ReorderSheetComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ReorderSheetData,
    private orderService: OrderService,
    private snackBar: MatSnackBar,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit() {
    this.orderService.getOrderHistory(this.data.storeId, this.data.userHash, '').subscribe(history => {
      this.orders = (history || []).slice(0, this.limit);
      this.cdr.markForCheck();
    }, () => {
      this.orders = [];
      this.loadFailed = true;
      this.cdr.markForCheck();
    });
  }

  reorder(order) {
    if (this.reorderingHash) return;
    this.reorderingHash = order.order_hash;
    this.cdr.markForCheck();

    this.orderService.reOrder(order.order_hash).subscribe(response => {
      this.reorderingHash = null;
      this.cdr.markForCheck();

      // Nothing survived the copy: an empty cart would look like the button was broken.
      if (response?.copied_items === 0) {
        this.snackBar.open('None of these items are still on the menu.', 'OK', { duration: 5000 });
        return;
      }

      // The backend copies what it can and reports what it dropped. Staying quiet would let a
      // shopper check out believing an item is in the bag when it is not.
      const skipped = (response?.skipped_items || []).map(s => s?.item).filter(Boolean);
      if (skipped.length) {
        this.snackBar.open(
          `${skipped.join(', ')} ${skipped.length === 1 ? 'is' : 'are'} no longer available and ${skipped.length === 1 ? 'was' : 'were'} left out.`,
          'OK', { duration: 6000 });
      }
      this.dialogRef.close();
      this.router.navigate(['order', response.order_hash]);
    }, () => {
      this.reorderingHash = null;
      this.cdr.markForCheck();
      this.snackBar.open('Could not start your reorder. Please try again.', 'OK', { duration: 4000 });
    });
  }

  viewAll() {
    this.dialogRef.close();
    this.router.navigate(['store', this.data.store?.url_slug || this.data.storeId, this.data.userHash, 'history']);
  }

  close() {
    this.dialogRef.close();
  }
}
